/* ===== game/boss-machine-art.ts — 공창의 관리자 · 원형의 기술 그림(레이저 · 설계선 · 설계 기둥) ===== */
import { TAU } from '../../engine/core/math.js';
import { STRIKE_ART, floorMark, hzGlow } from './boss-hazard-art.js';
import { BEAM_ART } from './boss-field-art.js';
/* ★ 가는 막대 하나로 그리지 않는다(점검판 2026-11-03 "레이저를 더 화려하게" · "원형 기술 끝이 잘린다 · 효과가 밋밋하다").
   광선은 끝이 허공에서 뚝 끊기지 않게 끝마디(치수 화살 · 지지는 불티)를 둔다. 사연: docs/code-history.md#h262 */

type C2 = CanvasRenderingContext2D;
const hs = (s: number, i: number) => { const v = Math.sin(s * 127.1 + i * 311.7) * 43758.5453; return v - Math.floor(v); };

Object.assign(BEAM_ART, {
  /** 레이저 — 붉은 번짐 · 떨리는 흰 심 · 흐르는 맥동 · 쏘는 렌즈의 섬광 · 끝에서 지지는 불티와 연기 */
  laser(c: C2, h: Bag, x: number, y: number, len: number, w: number, fade: number, T: number) {
    const op = c.globalCompositeOperation; c.globalCompositeOperation = 'lighter';
    const jit = 1 + Math.sin(T * 70) * 0.08 + Math.sin(T * 23) * 0.05;
    const band = (hw: number, col: string) => { const g = c.createLinearGradient(0, -hw, 0, hw); g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(0.5, col); g.addColorStop(1, 'rgba(0,0,0,0)'); c.fillStyle = g; c.fillRect(0, -hw, len, hw * 2); };
    band(w * 1.6 * jit, `rgba(255,30,40,${0.35 * fade})`);
    band(w * 0.7 * jit, `rgba(255,90,80,${0.75 * fade})`);
    band(w * 0.22, `rgba(255,245,240,${fade})`);
    for (let i = 0; i < 5; i++) {                                        // 흐르는 맥동
      const s = (T * 900 + i * len / 5) % len, g = c.createRadialGradient(s, 0, 0, s, 0, w * 1.2);
      g.addColorStop(0, `rgba(255,220,210,${0.6 * fade})`); g.addColorStop(1, 'rgba(255,60,60,0)'); c.fillStyle = g; c.fillRect(s - w * 1.2, -w * 1.2, w * 2.4, w * 2.4);
    }
    const fl = w * (2.4 + Math.sin(T * 50) * 0.4);                      // 렌즈 섬광 — 십자 빛
    c.fillStyle = `rgba(255,240,235,${0.9 * fade})`; c.fillRect(-fl * 1.6, -1.5, fl * 3.2, 3); c.fillRect(-1.5, -fl, 3, fl * 2);
    hzGlow(c, 0, 0, fl, '#ff6050', fade);
    hzGlow(c, len, 0, w * 2.2, '#ff8060', 0.9 * fade);                   // 끝 — 지지는 자리
    for (let i = 0; i < 12; i++) {
      const u = (T * 3 + hs(h.sd, i)) % 1, a = Math.PI + (hs(h.sd, i + 20) - 0.5) * 2.4, d = u * (30 + hs(h.sd, i + 30) * 40);
      c.strokeStyle = `rgba(255,${200 - u * 120},120,${(1 - u) * fade})`; c.lineWidth = 1.5;
      c.beginPath(); c.moveTo(len + Math.cos(a) * d, Math.sin(a) * d + u * u * 20); c.lineTo(len + Math.cos(a) * (d + 6), Math.sin(a) * (d + 6) + u * u * 20); c.stroke();
    }
    c.globalCompositeOperation = op;
    for (let i = 0; i < 4; i++) { const u = (T * 1.2 + hs(h.sd, i + 50)) % 1; c.fillStyle = `rgba(60,56,60,${0.35 * (1 - u) * fade})`; c.beginPath(); c.arc(len - u * 10, -u * 40, 4 + u * 10, 0, TAU); c.fill(); }
    c.fillStyle = '#3a3e48'; c.fillRect(-12, -10, 16, 20); c.fillStyle = '#ffd0c0'; c.fillRect(-3, -4, 6, 8);   // 쏘는 렌즈 받침
  },
  /** 설계선 — 빛나는 치수선: 모눈 띠 · 번호 눈금 · 흐르는 측정 점 · 양 끝 치수 화살(허공에서 끊기지 않게) */
  blueprint(c: C2, h: Bag, x: number, y: number, len: number, w: number, fade: number, T: number) {
    const op = c.globalCompositeOperation; c.globalCompositeOperation = 'lighter';
    const g = c.createLinearGradient(0, -w, 0, w); g.addColorStop(0, 'rgba(110,180,255,0)'); g.addColorStop(0.5, `rgba(150,210,255,${0.45 * fade})`); g.addColorStop(1, 'rgba(110,180,255,0)');
    c.fillStyle = g; c.fillRect(0, -w, len, w * 2);
    c.strokeStyle = `rgba(200,236,255,${0.35 * fade})`; c.lineWidth = 1;   // 모눈
    for (let s = (T * 60) % 15; s < len; s += 15) { c.beginPath(); c.moveTo(s, -w * 0.6); c.lineTo(s, w * 0.6); c.stroke(); }
    c.strokeStyle = `rgba(240,250,255,${fade})`; c.lineWidth = 2.2; c.beginPath(); c.moveTo(0, 0); c.lineTo(len, 0); c.stroke();
    c.lineWidth = 1.4;
    for (let s = 0, i = 0; s < len; s += 30, i++) { c.beginPath(); c.moveTo(s, -w * 0.5); c.lineTo(s, i % 3 ? -w * 0.1 : w * 0.5); c.stroke(); }
    c.font = 'bold 10px monospace'; c.fillStyle = `rgba(230,246,255,${0.85 * fade})`;
    for (let s = 90, i = 1; s < len - 40; s += 90, i++) c.fillText(String(i * 30), s + 3, -w * 0.6);
    for (let i = 0; i < 3; i++) { const s = (T * 500 + i * len / 3) % len; hzGlow(c, s, 0, w * 0.9, '#cfefff', 0.9 * fade); }
    c.fillStyle = `rgba(240,250,255,${fade})`;                         // 치수 화살 — 양 끝
    for (const [ex, d] of [[0, 1], [len, -1]] as [number, number][]) { c.beginPath(); c.moveTo(ex, 0); c.lineTo(ex + d * 14, -6); c.lineTo(ex + d * 14, 6); c.closePath(); c.fill(); c.fillRect(ex - 1, -w * 0.9, 2, w * 1.8); }
    hzGlow(c, len, 0, w * 1.8, '#bfe0ff', 0.8 * fade);
    c.globalCompositeOperation = op;
  }
});

Object.assign(STRIKE_ART, {
  /** 설계 기둥 — 윤곽선이 바닥에서 위로 그려지고(설계) · 상아빛 재질이 차오른 뒤(주조) · 끝에 조각나 흩어진다 */
  draft: {
    tell(c: C2, h: Bag, x: number, y: number, k: number, T: number) {
      floorMark(c, x, y, h.w * 0.6, '#bfe0ff', 0.2 + k * 0.4);
      c.strokeStyle = `rgba(200,236,255,${0.5 + k * 0.5})`; c.lineWidth = 1.2; c.setLineDash([5, 4]); c.lineDashOffset = -T * 30;
      const hh = h.hh * k, hw = h.w * 0.4;
      c.beginPath(); c.moveTo(x - hw, y); c.lineTo(x - hw, y - hh); c.moveTo(x + hw, y); c.lineTo(x + hw, y - hh); c.stroke(); c.setLineDash([]);
      c.beginPath(); c.moveTo(x - hw - 10, y + 6); c.lineTo(x + hw + 10, y + 6); c.stroke();   // 치수선
      hzGlow(c, x - hw, y - hh, 5, '#cfefff', 0.9); hzGlow(c, x + hw, y - hh, 5, '#cfefff', 0.9);
    },
    hit(c: C2, h: Bag, x: number, y: number, f: number, T: number) {
      const hw = h.w * 0.42, up = Math.min(1, f * 5), hh = h.hh * up, end = Math.max(0, f - 0.7) / 0.3;
      c.globalAlpha = 1 - end;
      const g = c.createLinearGradient(x - hw, 0, x + hw, 0); g.addColorStop(0, '#b8ac90'); g.addColorStop(0.4, '#f8f0dc'); g.addColorStop(1, '#a89c80');
      c.fillStyle = g; c.fillRect(x - hw, y - hh, hw * 2, hh);
      c.strokeStyle = '#2a3448'; c.lineWidth = 1.4; c.strokeRect(x - hw, y - hh, hw * 2, hh);
      c.strokeStyle = 'rgba(80,130,200,.6)'; c.lineWidth = 1;            // 설계 마디선
      for (let yy = y - 20; yy > y - hh; yy -= 20) { c.beginPath(); c.moveTo(x - hw, yy); c.lineTo(x + hw, yy); c.stroke(); }
      c.fillStyle = '#e8dcc0'; c.fillRect(x - hw - 4, y - hh - 6, hw * 2 + 8, 6); c.strokeRect(x - hw - 4, y - hh - 6, hw * 2 + 8, 6);   // 머리돌
      c.globalCompositeOperation = 'lighter';
      const sw = (T * 2 + hs(h.sd, 1)) % 1; c.fillStyle = 'rgba(190,225,255,.5)'; c.fillRect(x - hw, y - hh * sw - 3, hw * 2, 6);   // 훑는 측정 빛
      c.globalCompositeOperation = 'source-over';
      for (let i = 0; i < 8 && end > 0; i++) {                           // 끝 — 자 토막으로 흩어진다
        const a = -Math.PI * hs(h.sd, i), d = end * (20 + hs(h.sd, i + 8) * 40);
        c.save(); c.translate(x + Math.cos(a) * d, y - hh * hs(h.sd, i + 16) + Math.sin(a) * d); c.rotate(end * 6 * (hs(h.sd, i + 24) - 0.5));
        c.fillStyle = '#f4ead0'; c.fillRect(-8, -2, 16, 4); c.restore();
      }
      c.globalAlpha = 1;
    }
  }
});
